import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { createCoupon, deleteCoupon, getCoupons, importCouponsFromCsv, updateCoupon } from "../../../services/couponsService";
import { formatCount, formatShortDate, hasStripeAccount } from "../../../helpers/functions";
import { RevAlert, RevButton, RevCard, RevChip, RevEmpty, RevField, RevKvRow, RevModal, RevPagination, RevProgress, RevSegmented, RevSkeleton, RevSwitch } from "../../../components/ds";

const CODE_LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const CODE_DIGITS = "23456789";
const CODE_RE = /^[A-Z0-9]{4,10}$/;
const DEFAULT_FORM = { code: "", type: "pct", value: "20", limit: "100", unlimited: false, expiry: "" };
const VALUE_BY_TYPE = { pct: "20", eur: "5,00", photos: "2", pack: "" };
const PAGE_SIZE = 20;
const DEFAULT_IMPORT = { limit: "", unlimited: false, expiry: "", update: false };
const CSV_SAMPLE = "CUPON,VALOR,TIPO\nVERANO001,20,porcentaje\nNAVIDAD001,10,importe\nPRIMAVERA,2,fotos\nCLUB,1,pack";

const toNumber = (value) => parseFloat(String(value).replace(",", "."));
// Fecha de un input type=date a partir de los segundos unix del backend (misma base UTC que al crear)
const toDateInput = (seconds) => new Date(seconds * 1000).toISOString().split("T")[0];

// Estado de un cupón tal y como lo devuelve GET /coupons (contadores del backend)
const couponStatus = (coupon) => {
    if (!coupon.isActive) return "disabled";
    if (coupon.redeemBy && coupon.redeemBy * 1000 < Date.now()) return "expired";
    if (coupon.maxRedemptions && coupon.confirmedRedemptions >= coupon.maxRedemptions) return "exhausted";
    return "redeemable";
};
const STATUS_TONE = { redeemable: "ok", exhausted: "warn", expired: "warn", disabled: undefined };

// Tipo y valor del formulario a partir de un cupón existente (para editarlo)
const formFromCoupon = (coupon) => {
    let type = "pct";
    let value = "";
    if (coupon.percentOff !== null && coupon.percentOff !== undefined) {
        type = "pct";
        value = String(coupon.percentOff);
    } else if (coupon.amountOff !== null && coupon.amountOff !== undefined) {
        type = "eur";
        value = (coupon.amountOff / 100).toFixed(2).replace(".", ",");
    } else if (coupon.freePhotos) {
        type = "photos";
        value = String(coupon.freePhotos);
    } else if (coupon.freePack) {
        type = "pack";
    }
    return {
        code: coupon.code,
        type,
        value,
        limit: coupon.maxRedemptions ? String(coupon.maxRedemptions) : "",
        unlimited: !coupon.maxRedemptions,
        expiry: coupon.redeemBy ? toDateInput(coupon.redeemBy) : "",
    };
};

const EventCoupons = ({ eventId, selectedEvent, user }) => {
    const { t, i18n } = useTranslation();
    const [form, setForm] = useState(DEFAULT_FORM);
    const [editing, setEditing] = useState(null);
    const [sending, setSending] = useState(false);
    const [duplicate, setDuplicate] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [coupons, setCoupons] = useState(null);
    const [listError, setListError] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [busyCode, setBusyCode] = useState(null);
    const [copiedCode, setCopiedCode] = useState(null);
    const [toDelete, setToDelete] = useState(null);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(0);
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importFileError, setImportFileError] = useState(null);
    const [importForm, setImportForm] = useState(DEFAULT_IMPORT);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const formCardRef = useRef(null);

    const today = new Date().toISOString().split("T")[0];
    const currencyCode = user?.currency || "EUR";
    const money = useMemo(() => {
        try {
            return new Intl.NumberFormat(i18n.language, { style: "currency", currency: currencyCode });
        } catch (e) {
            return null;
        }
    }, [i18n.language, currencyCode]);

    // Importar por CSV exige cuenta de cobros (el backend devuelve 400 sin ella); crear y gestionar no
    const stripeMissing = Boolean(user) && !hasStripeAccount(user);

    // Evita setState tras desmontar (la pestaña se re-monta al cargar el evento)
    const alive = useRef(true);
    useEffect(() => {
        alive.current = true;
        return () => { alive.current = false; };
    }, []);

    const loadCoupons = useCallback(async () => {
        if (!eventId) return;
        setListError(false);
        try {
            const data = await getCoupons(eventId);
            if (alive.current) setCoupons(Array.isArray(data?.coupons) ? data.coupons : []);
        } catch (err) {
            if (!alive.current) return;
            setCoupons((prev) => prev || []);
            setListError(true);
        }
    }, [eventId]);

    useEffect(() => {
        setCoupons(null);
        setEditing(null);
        setForm(DEFAULT_FORM);
        setQuery("");
        setPage(0);
        loadCoupons();
    }, [loadCoupons]);

    useEffect(() => {
        if (!copiedCode) return undefined;
        const timer = setTimeout(() => setCopiedCode(null), 2000);
        return () => clearTimeout(timer);
    }, [copiedCode]);

    useEffect(() => {
        if (!notice) return undefined;
        let live = true;
        const timer = setTimeout(() => {
            if (live) setNotice(null);
        }, 4000);
        return () => {
            live = false;
            clearTimeout(timer);
        };
    }, [notice]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const codeOk = CODE_RE.test(form.code);
    const valueNum = toNumber(form.value);
    const valueOk = form.type === "pack"
        ? true
        : form.type === "pct"
            ? valueNum > 0 && valueNum <= 100
            : form.type === "eur"
                ? valueNum > 0
                : Number.isInteger(valueNum) && valueNum >= 1;
    // Usos máximos obligatorios salvo «Sin límite» explícito: dejar el campo vacío ya no equivale a ilimitado
    // (pasó con códigos de precompra que se canjearon varias veces)
    const limitNum = !form.unlimited && form.limit ? parseInt(form.limit, 10) : null;
    const limitOk = form.unlimited || limitNum >= 1;
    const expiryOk = !form.expiry || form.expiry >= today;
    // Al editar, el código viene del cupón y no se valida con el formato de los nuevos (los importados pueden ser más largos)
    const canSubmit = (editing ? true : codeOk) && valueOk && limitOk && expiryOk;

    const packPrice = selectedEvent?.pricingData?.fullSelfiePrice;
    const packOn = packPrice > 0;
    const unitPrice = selectedEvent?.pricingData?.unitPrice;

    const typeItems = [
        { value: "pct", label: t("eventCoupons.type.pct") },
        { value: "eur", label: t("eventCoupons.type.eur") },
        { value: "photos", label: t("eventCoupons.type.photos") },
        { value: "pack", label: t("eventCoupons.type.pack") },
    ];
    const typeNotes = {
        pct: t("eventCoupons.typeNote.pct"),
        eur: t("eventCoupons.typeNote.eur"),
        photos: t("eventCoupons.typeNote.photos"),
        pack: t("eventCoupons.typeNote.pack"),
    };
    const valueLabels = {
        pct: t("eventCoupons.valueLabel.pct"),
        eur: t("eventCoupons.valueLabel.eur"),
        photos: t("eventCoupons.valueLabel.photos"),
    };
    const valueNotes = {
        pct: t("eventCoupons.valueNote.pct"),
        eur: t("eventCoupons.valueNote.eur", { currency: currencyCode.toUpperCase() }),
        photos: t("eventCoupons.valueNote.photos"),
    };

    const perRedemption = form.type === "eur"
        ? (valueOk ? valueNum : null)
        : form.type === "photos"
            ? (valueOk && unitPrice > 0 ? valueNum * unitPrice : null)
            : form.type === "pack"
                ? (packOn ? packPrice : null)
                : null;

    let summary;
    if (!editing && !codeOk) {
        summary = t("eventCoupons.summary.missingCode");
    } else if (form.unlimited) {
        summary = t("eventCoupons.summary.unlimited");
    } else if (!limitNum) {
        summary = t("eventCoupons.summary.missingLimit");
    } else if (form.type === "pct") {
        summary = t("eventCoupons.summary.percent", { count: limitNum, pct: form.value });
    } else if (perRedemption > 0 && money) {
        summary = t("eventCoupons.summary.withLimit", { count: limitNum, amount: money.format(perRedemption * limitNum) });
    } else {
        summary = t("eventCoupons.summary.limitNoAmount", { count: limitNum });
    }

    const codeHelp = editing
        ? t("eventCoupons.edit.codeHelp")
        : duplicate
            ? t("eventCoupons.code.help.duplicate")
            : t("eventCoupons.code.help.format");
    const codeInvalid = !editing && (!codeOk || duplicate);

    const onCodeChange = (e) => {
        setDuplicate(false);
        set({ code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") });
    };

    const onValueChange = (e) => {
        let value = e.target.value;
        if (form.type === "eur") {
            value = value.replace(/\./g, ",");
            if (!/^\d*(,\d*)?$/.test(value)) return;
            const [integer, decimals] = value.split(",");
            value = decimals === undefined ? integer : `${integer},${decimals.slice(0, 2)}`;
        }
        set({ value });
    };

    const generateCode = () => {
        let code = "";
        for (let i = 0; i < 4; i++) code += CODE_LETTERS[Math.floor(Math.random() * CODE_LETTERS.length)];
        for (let i = 0; i < 2; i++) code += CODE_DIGITS[Math.floor(Math.random() * CODE_DIGITS.length)];
        setDuplicate(false);
        set({ code });
    };

    // Campos del descuento según el tipo elegido (los otros tres se vacían: el backend exige exactamente uno)
    const discountFields = () => {
        const fields = { percentOff: null, amountOff: null, freePhotos: null, freePack: false };
        if (form.type === "pct") {
            fields.percentOff = toNumber(form.value);
        } else if (form.type === "eur") {
            fields.amountOff = Math.round(toNumber(form.value) * 100);
            fields.currency = currencyCode.toLowerCase();
        } else if (form.type === "photos") {
            fields.freePhotos = parseInt(form.value, 10);
        } else {
            fields.freePack = true;
        }
        return fields;
    };

    const startEdit = (coupon) => {
        setEditing(coupon);
        setForm(formFromCoupon(coupon));
        setDuplicate(false);
        setError(null);
        formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const cancelEdit = () => {
        setEditing(null);
        setForm(DEFAULT_FORM);
        setError(null);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit || sending) return;
        setSending(true);
        setError(null);
        setDuplicate(false);
        const limit = limitNum;
        const redeemBy = form.expiry ? new Date(form.expiry).getTime() / 1000 : null;
        try {
            if (editing) {
                // PUT sobreescribe el cupón entero: se parte del objeto del GET (con eventId e isGlobal) y se cambian los campos editados
                await updateCoupon(editing.code, { ...editing, maxRedemptions: limit, redeemBy, ...discountFields() });
                setNotice({ kind: "saved", code: editing.code });
                setEditing(null);
                setForm(DEFAULT_FORM);
            } else {
                const code = form.code.toUpperCase();
                await createCoupon({
                    code,
                    maxRedemptions: limit || undefined,
                    redeemBy: redeemBy || undefined,
                    isGlobal: false,
                    eventId,
                    isActive: true,
                    ...discountFields(),
                });
                setForm(DEFAULT_FORM);
                setNotice({ kind: "created", code });
            }
            loadCoupons();
        } catch (err) {
            if (!editing && err?.response?.status === 400) {
                setDuplicate(true);
            } else {
                setError(err?.response?.data?.error || t(editing ? "eventCoupons.edit.error" : "eventCoupons.error"));
            }
        } finally {
            setSending(false);
        }
    };

    const runAction = async (code, action) => {
        setBusyCode(code);
        setActionError(null);
        try {
            await action();
            await loadCoupons();
        } catch (err) {
            if (alive.current) setActionError(err?.response?.data?.error || t("eventCoupons.list.actionError"));
        } finally {
            if (alive.current) setBusyCode(null);
        }
    };

    // PUT sobreescribe el cupón entero: se manda tal cual vino del GET (con eventId), solo cambia isActive
    const toggleActive = (coupon) => runAction(coupon.code, () => updateCoupon(coupon.code, { ...coupon, isActive: !coupon.isActive }));

    const confirmDelete = () => {
        const code = toDelete;
        setToDelete(null);
        if (editing?.code === code) cancelEdit();
        runAction(code, () => deleteCoupon(code));
    };

    const copyCode = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedCode(code);
        } catch (err) {
            setActionError(t("eventCoupons.list.copyError"));
        }
    };

    // ---- Importación CSV ----
    const openImport = () => {
        setImportFile(null);
        setImportFileError(null);
        setImportForm(DEFAULT_IMPORT);
        setImportError(null);
        setImportOpen(true);
    };
    const closeImport = () => {
        if (importing) return;
        setImportOpen(false);
    };
    const setImport = (patch) => setImportForm((prev) => ({ ...prev, ...patch }));

    const onCsvSelected = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setImportError(null);
        // Por extensión, no por MIME: Excel etiqueta los .csv como application/vnd.ms-excel
        if (!/\.csv$/i.test(file.name)) {
            setImportFile(null);
            setImportFileError(t("eventCoupons.import.file.notCsv"));
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const content = String(ev.target.result || "");
            if (content.split(/\r?\n/).filter((line) => line.trim()).length < 2) {
                setImportFile(null);
                setImportFileError(t("eventCoupons.import.file.empty"));
                return;
            }
            setImportFileError(null);
            setImportFile({ name: file.name, content });
        };
        reader.onerror = () => {
            setImportFile(null);
            setImportFileError(t("eventCoupons.import.file.readError"));
        };
        reader.readAsText(file);
    };

    const importLimitNum = !importForm.unlimited && importForm.limit ? parseInt(importForm.limit, 10) : null;
    const importLimitOk = importForm.unlimited || importLimitNum >= 1;
    const importExpiryOk = !importForm.expiry || importForm.expiry >= today;
    const canImport = Boolean(importFile) && importLimitOk && importExpiryOk && !stripeMissing;

    const runImport = async () => {
        if (!canImport || importing) return;
        setImporting(true);
        setImportError(null);
        try {
            // eventId siempre el de la ruta: sin él el backend crearía cupones globales
            const result = await importCouponsFromCsv({
                csvContent: importFile.content.trim(),
                eventId,
                maxRedemptions: importLimitNum || undefined,
                redeemBy: importForm.expiry ? new Date(importForm.expiry).getTime() / 1000 : undefined,
                // Los códigos que ya existen en este evento quedan como en el archivo y el formulario, sin tocar sus canjes
                updateExisting: importForm.update || undefined,
            });
            if (!alive.current) return;
            setImportOpen(false);
            setImportResult(result || {});
            setQuery("");
            setPage(0);
            loadCoupons();
        } catch (err) {
            if (alive.current) setImportError(err?.response?.data?.error || t("eventCoupons.import.error"));
        } finally {
            if (alive.current) setImporting(false);
        }
    };

    const couponDetail = (coupon) => {
        let value;
        if (coupon.percentOff !== null && coupon.percentOff !== undefined) {
            value = t("eventCoupons.list.detail.pct", { pct: coupon.percentOff });
        } else if (coupon.amountOff !== null && coupon.amountOff !== undefined) {
            let amountFmt;
            try {
                amountFmt = new Intl.NumberFormat(i18n.language, { style: "currency", currency: (coupon.currency || currencyCode).toUpperCase() })
                    .format(coupon.amountOff / 100);
            } catch (e) {
                amountFmt = (coupon.amountOff / 100).toFixed(2);
            }
            value = t("eventCoupons.list.detail.amount", { amount: amountFmt });
        } else if (coupon.freePhotos) {
            value = coupon.freePhotos === 1
                ? t("eventCoupons.list.detail.photosOne")
                : t("eventCoupons.list.detail.photos", { count: coupon.freePhotos });
        } else if (coupon.freePack) {
            value = t("eventCoupons.list.detail.pack");
        } else {
            value = "—";
        }
        return coupon.redeemBy
            ? t("eventCoupons.list.detail.until", { value, date: formatShortDate(coupon.redeemBy * 1000, i18n.language) })
            : t("eventCoupons.list.detail.noExpiry", { value });
    };

    const usesText = (coupon) => {
        const used = formatCount(coupon.confirmedRedemptions || 0, i18n.language);
        return coupon.maxRedemptions
            ? t("eventCoupons.list.uses", { used, max: formatCount(coupon.maxRedemptions, i18n.language) })
            : t("eventCoupons.list.usesUnlimited", { used });
    };

    const list = coupons || [];
    const activeCount = list.filter((c) => couponStatus(c) === "redeemable").length;
    const redemptions = list.reduce((acc, c) => acc + (c.confirmedRedemptions || 0), 0);
    const unlimited = list.some((c) => c.isActive && !c.maxRedemptions);
    const remaining = list.reduce((acc, c) => acc + (c.maxRedemptions ? Math.max(0, c.maxRedemptions - (c.confirmedRedemptions || 0)) : 0), 0);
    const delivered = list.reduce((acc, c) => acc + (Number(c.totalDiscountApplied) || 0), 0);
    const deliveredText = money ? money.format(delivered) : delivered.toFixed(2);
    const kv = (value) => (coupons === null ? "—" : value);

    // Buscador por código y paginación en cliente (la importación CSV mete cientos de códigos)
    const needle = query.trim().toUpperCase();
    const filtered = needle ? list.filter((c) => String(c.code).toUpperCase().includes(needle)) : list;
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, pages - 1);
    const pageItems = filtered.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);
    const onQueryChange = (e) => {
        setQuery(e.target.value);
        setPage(0);
    };

    const importSummary = importResult?.summary || {};
    const importSuccess = importSummary.successCount || 0;
    const importUpdated = importSummary.updatedCount || 0;
    const importFailed = (importSummary.errorCount || 0) + (importSummary.duplicateCount || 0);
    const importTone = importSuccess + importUpdated > 0 ? (importFailed > 0 ? "warn" : "ok") : "err";
    const sampleChips = (samples, total, tone, getCode, getTitle) => {
        const items = Array.isArray(samples) ? samples : [];
        const more = Math.max(0, (total || 0) - items.length);
        return (
            <div className="rev-cup-chips">
                {items.map((item, idx) => (
                    <RevChip key={`${getCode(item)}-${idx}`} tone={tone} title={getTitle ? getTitle(item) : undefined}>
                        <span className="rev-mono">{getCode(item)}</span>
                    </RevChip>
                ))}
                {more > 0 && <span className="rev-help">{t("eventCoupons.importResult.more", { count: more })}</span>}
            </div>
        );
    };

    return (
        <div className="rev-cols rev-cols--flush">
            <div className="rev-cols__main">
                {stripeMissing &&
                    <RevAlert tone="warn" title={t("eventCoupons.stripe.title")}
                        actions={<RevButton as={Link} to="/account/photo-sale/home" size="sm">{t("eventCoupons.stripe.cta")}</RevButton>}>
                        <span className="rev-help">{t("eventCoupons.stripe.body")}</span>
                    </RevAlert>}
                <form onSubmit={onSubmit} ref={formCardRef}>
                    <RevCard
                        title={editing ? t("eventCoupons.edit.title", { code: editing.code }) : t("eventCoupons.new.title")}
                        foot={<>
                            <span className="rev-help rev-grow">{summary}</span>
                            <div className="rev-card__foot-actions">
                                {editing &&
                                    <RevButton onClick={cancelEdit} disabled={sending}>{t("eventCoupons.edit.cancel")}</RevButton>}
                                <RevButton variant="primary" type="submit" disabled={!canSubmit} loading={sending}>
                                    {editing ? t("eventCoupons.edit.submit") : t("eventCoupons.submit")}
                                </RevButton>
                            </div>
                        </>}>
                        <span className="rev-help rev-help--tight">
                            {editing
                                ? t("eventCoupons.edit.intro")
                                : t("eventCoupons.new.intro", { name: selectedEvent?.name || "—" })}
                        </span>
                        {notice &&
                            <RevAlert tone="ok" title={t(notice.kind === "saved" ? "eventCoupons.edit.saved.title" : "eventCoupons.created.title", { code: notice.code })}>
                                <span className="rev-help">{t(notice.kind === "saved" ? "eventCoupons.edit.saved.body" : "eventCoupons.created.body")}</span>
                            </RevAlert>}
                        {error && <RevAlert tone="err">{error}</RevAlert>}
                        <div className="rev-form-grid">
                            <RevField label={t("eventCoupons.code.label")} htmlFor="couponCode"
                                help={codeHelp} helpTone={codeInvalid ? "err" : undefined}>
                                <div className="rev-field-row rev-field-row--gap">
                                    <input
                                        id="couponCode"
                                        type="text"
                                        maxLength={editing ? undefined : 10}
                                        autoComplete="off"
                                        readOnly={Boolean(editing)}
                                        className={`rev-input rev-input--code rev-grow rev-minw0${codeInvalid ? " rev-input--error" : ""}${editing ? " rev-input--readonly" : ""}`}
                                        placeholder={t("eventCoupons.code.placeholder")}
                                        value={form.code}
                                        onChange={onCodeChange} />
                                    {!editing &&
                                        <RevButton variant="secondary" onClick={generateCode}>
                                            {t("event.new.code.generate")}
                                        </RevButton>}
                                </div>
                            </RevField>
                            <RevField label={t("eventCoupons.discount.label")} help={typeNotes[form.type]}>
                                <RevSegmented
                                    className="rev-segmented--fill rev-cup-type"
                                    items={typeItems}
                                    value={form.type}
                                    onChange={(value) => set({ type: value, value: VALUE_BY_TYPE[value] })} />
                            </RevField>
                            {form.type !== "pack" &&
                                <RevField label={valueLabels[form.type]} htmlFor="couponValue" help={valueNotes[form.type]}>
                                    <input
                                        id="couponValue"
                                        type="text"
                                        inputMode="decimal"
                                        className={`rev-input rev-num rev-cup-value${valueOk ? "" : " rev-input--error"}`}
                                        value={form.value}
                                        onChange={onValueChange} />
                                </RevField>}
                            {form.type === "pack" &&
                                <RevAlert tone="info">
                                    {packOn && money
                                        ? t("eventCoupons.packNote.on", { price: money.format(packPrice) })
                                        : t("eventCoupons.packNote.off")}
                                </RevAlert>}
                            <RevField label={t("eventCoupons.limit.label")} htmlFor={form.unlimited ? "couponUnlimited" : "couponLimit"}
                                help={t(form.unlimited ? "eventCoupons.limit.unlimitedHelp" : "eventCoupons.limit.help")}
                                helpTone={form.unlimited ? "warn" : undefined}>
                                {!form.unlimited &&
                                    <input
                                        id="couponLimit"
                                        type="text"
                                        inputMode="numeric"
                                        className={`rev-input rev-num rev-cup-value${limitOk || !form.limit ? "" : " rev-input--error"}`}
                                        value={form.limit}
                                        onChange={(e) => set({ limit: e.target.value.replace(/[^0-9]/g, "") })} />}
                                <label className="rev-switch-field" htmlFor="couponUnlimited">
                                    <input
                                        type="checkbox"
                                        id="couponUnlimited"
                                        className="rev-switch-input"
                                        checked={form.unlimited}
                                        onChange={(e) => set({ unlimited: e.target.checked })} />
                                    <RevSwitch on={form.unlimited} />
                                    <span>{t("eventCoupons.limit.unlimited")}</span>
                                </label>
                            </RevField>
                            <RevField label={t("eventCoupons.expiry.label")} htmlFor="couponExpiry" help={t("eventCoupons.expiry.help")}>
                                <input
                                    id="couponExpiry"
                                    type="date"
                                    min={today}
                                    className={`rev-input rev-num${expiryOk ? "" : " rev-input--error"}`}
                                    value={form.expiry}
                                    onChange={(e) => set({ expiry: e.target.value })} />
                            </RevField>
                        </div>
                    </RevCard>
                </form>
                <RevCard title={t("eventCoupons.list.title")}
                    head={<div className="rev-cup-tools">
                        {list.length > 0 &&
                            <input
                                type="search"
                                className="rev-input rev-input--code rev-cup-search"
                                placeholder={t("eventCoupons.search.placeholder")}
                                aria-label={t("eventCoupons.search.label")}
                                value={query}
                                onChange={onQueryChange} />}
                        <RevButton variant="secondary" onClick={openImport} disabled={stripeMissing}
                            title={stripeMissing ? t("eventCoupons.stripe.title") : undefined}>
                            {t("eventCoupons.import.open")}
                        </RevButton>
                    </div>}>
                    {listError && <RevAlert tone="err" actions={<RevButton size="sm" onClick={loadCoupons}>{t("eventCoupons.list.retry")}</RevButton>}>{t("eventCoupons.list.loadError")}</RevAlert>}
                    {actionError && <RevAlert tone="err">{actionError}</RevAlert>}
                    {coupons === null && !listError && <RevSkeleton.Cuerpo
                        tipo="filas"
                        filas={3}
                        celdas={['stack', 'stack', 'chip', 'btn-sm']}
                        clases={{ lista: 'rev-cup-list', fila: 'rev-cup-row rev-cup-row--esqueleto' }}
                        label={t('event.photos.loading')} />}
                    {coupons !== null && list.length === 0 && !listError &&
                        <RevEmpty icon="confirmation_number" title={t("eventCoupons.list.empty.title")} body={t("eventCoupons.list.empty.body")} />}
                    {list.length > 0 && filtered.length === 0 &&
                        <RevEmpty icon="search_off" title={t("eventCoupons.search.empty.title", { query: query.trim() })} body={t("eventCoupons.search.empty.body")}>
                            <RevButton size="sm" onClick={() => onQueryChange({ target: { value: "" } })}>{t("eventCoupons.search.clear")}</RevButton>
                        </RevEmpty>}
                    {pageItems.length > 0 &&
                        <div className="rev-cup-list">
                            {pageItems.map((coupon) => {
                                const status = couponStatus(coupon);
                                const uses = usesText(coupon);
                                const busy = busyCode === coupon.code;
                                // Sin eventId en la respuesta (backend anterior) el PUT globalizaría el cupón: no se ofrece editar ni activar
                                const canEdit = coupon.isGlobal || Object.prototype.hasOwnProperty.call(coupon, "eventId");
                                return (
                                    <div className={`rev-cup-row${editing?.code === coupon.code ? " rev-cup-row--editing" : ""}`} key={coupon.code}>
                                        <div className="rev-cup-row__id">
                                            <span className="rev-mono">{coupon.code}</span>
                                            <span className="rev-help">{couponDetail(coupon)}</span>
                                        </div>
                                        <div className="rev-cup-row__usage">
                                            <span className="rev-help">{uses}</span>
                                            {coupon.maxRedemptions
                                                ? <RevProgress value={Math.min(coupon.confirmedRedemptions || 0, coupon.maxRedemptions)} max={coupon.maxRedemptions} aria-label={uses} />
                                                : <RevProgress value={0} max={100} aria-label={uses} />}
                                        </div>
                                        <div className="rev-cup-row__chips">
                                            <RevChip tone={STATUS_TONE[status]}>{t(`eventCoupons.list.status.${status}`)}</RevChip>
                                            {coupon.isGlobal && <RevChip>{t("eventCoupons.list.global")}</RevChip>}
                                            {coupon.importedFromCsv && <RevChip>{t("eventCoupons.list.imported")}</RevChip>}
                                        </div>
                                        <div className="rev-cup-row__actions">
                                            <span className="rev-switch-field">
                                                <RevSwitch
                                                    as="button"
                                                    role="switch"
                                                    bare
                                                    on={coupon.isActive}
                                                    aria-checked={coupon.isActive}
                                                    aria-label={t("eventCoupons.list.activeFor", { code: coupon.code })}
                                                    title={canEdit ? undefined : t("eventCoupons.list.toggleUnavailable")}
                                                    disabled={busy || !canEdit}
                                                    onClick={() => toggleActive(coupon)} />
                                                {t("eventCoupons.list.active")}
                                            </span>
                                            <RevButton size="sm" onClick={() => copyCode(coupon.code)}>
                                                {copiedCode === coupon.code ? t("eventCoupons.list.copied") : t("eventCoupons.list.copy")}
                                            </RevButton>
                                            <RevButton size="sm" disabled={busy || !canEdit || editing?.code === coupon.code}
                                                title={canEdit ? undefined : t("eventCoupons.list.toggleUnavailable")}
                                                onClick={() => startEdit(coupon)}>
                                                {t("eventCoupons.list.edit")}
                                            </RevButton>
                                            <RevButton size="sm" variant="danger" disabled={busy} onClick={() => setToDelete(coupon.code)}>
                                                {t("eventCoupons.list.delete")}
                                            </RevButton>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>}
                    {filtered.length > PAGE_SIZE &&
                        <div className="rev-cup-pager">
                            <span className="rev-help rev-num">
                                {t("eventCoupons.pagination.range", {
                                    start: formatCount(currentPage * PAGE_SIZE + 1, i18n.language),
                                    end: formatCount(Math.min(filtered.length, (currentPage + 1) * PAGE_SIZE), i18n.language),
                                    total: formatCount(filtered.length, i18n.language),
                                })}
                            </span>
                            <RevPagination page={currentPage} pages={pages} onChange={setPage}
                                prevLabel={t("eventCoupons.pagination.prev")} nextLabel={t("eventCoupons.pagination.next")} />
                        </div>}
                </RevCard>
            </div>
            <div className="rev-cols__aside">
                <RevCard title={t("eventCoupons.spent.title")}>
                    <RevKvRow className="rev-cup-kv" k={t("eventCoupons.spent.activeCoupons")} v={kv(formatCount(activeCount, i18n.language))} num muted={coupons === null} />
                    <RevKvRow className="rev-cup-kv" k={t("eventCoupons.spent.redemptions")} v={kv(formatCount(redemptions, i18n.language))} num muted={coupons === null} />
                    <RevKvRow className="rev-cup-kv" k={t("eventCoupons.spent.remaining")}
                        v={kv(unlimited ? t("eventCoupons.spent.unlimited") : formatCount(remaining, i18n.language))} num={!unlimited} muted={coupons === null} />
                    <RevKvRow className="rev-cup-kv" k={t("eventCoupons.spent.delivered")}
                        v={kv(delivered > 0 ? `−${deliveredText}` : deliveredText)} num muted={coupons === null} tone={delivered > 0 ? "warn" : undefined} />
                    <span className="rev-help">{t("eventCoupons.spent.foot")}</span>
                </RevCard>
                <RevCard title={t("eventCoupons.tips.title")}>
                    <span className="rev-card__text rev-cup-tip">{t("eventCoupons.tips.short")}</span>
                    <span className="rev-card__text rev-cup-tip">{t("eventCoupons.tips.limit")}</span>
                    <span className="rev-card__text rev-cup-tip">{t("eventCoupons.tips.import")}</span>
                </RevCard>
            </div>
            {toDelete &&
                <RevModal width={420} onClose={() => setToDelete(null)} closeLabel={t("eventCoupons.delete.cancel")}
                    title={t("eventCoupons.delete.title", { code: toDelete })}>
                    <span className="rev-help">{t("eventCoupons.delete.body")}</span>
                    <div className="rev-cup-confirm__actions">
                        <RevButton onClick={() => setToDelete(null)}>{t("eventCoupons.delete.cancel")}</RevButton>
                        <RevButton variant="danger" onClick={confirmDelete}>{t("eventCoupons.delete.confirm")}</RevButton>
                    </div>
                </RevModal>}
            {importOpen &&
                <RevModal width={520} onClose={closeImport} closable={!importing} closeLabel={t("eventCoupons.import.cancel")}
                    title={t("eventCoupons.import.title")}
                    footer={<>
                        <RevButton onClick={closeImport} disabled={importing}>{t("eventCoupons.import.cancel")}</RevButton>
                        <RevButton variant="primary" onClick={runImport} disabled={!canImport} loading={importing}>
                            {t("eventCoupons.import.submit")}
                        </RevButton>
                    </>}>
                    <div className="rev-modal__body">
                        <p>{t("eventCoupons.import.intro", { name: selectedEvent?.name || "—" })}</p>
                        {importError && <RevAlert tone="err">{importError}</RevAlert>}
                        <RevField label={t("eventCoupons.import.file.label")}
                            help={importFileError || (importFile ? t("eventCoupons.import.file.loaded", { name: importFile.name }) : t("eventCoupons.import.file.help"))}
                            helpTone={importFileError ? "err" : undefined}>
                            <RevButton as="label" htmlFor="couponCsv" variant="secondary" fit>
                                {t("generic.selectFileButton.text")}
                            </RevButton>
                            <input id="couponCsv" type="file" accept=".csv,text/csv,text/plain" className="rev-hidden" onChange={onCsvSelected} />
                        </RevField>
                        <RevField label={t("eventCoupons.import.format.label")} help={t("eventCoupons.import.format.help")}>
                            <pre className="rev-mono rev-cup-sample">{CSV_SAMPLE}</pre>
                        </RevField>
                        <div className="rev-form-grid">
                            <RevField label={t("eventCoupons.import.limit.label")} htmlFor={importForm.unlimited ? "importUnlimited" : "importLimit"}
                                help={t(importForm.unlimited ? "eventCoupons.limit.unlimitedHelp" : "eventCoupons.import.limit.help")}
                                helpTone={importForm.unlimited ? "warn" : undefined}>
                                {!importForm.unlimited &&
                                    <input
                                        id="importLimit"
                                        type="text"
                                        inputMode="numeric"
                                        className={`rev-input rev-num rev-cup-value${importLimitOk || !importForm.limit ? "" : " rev-input--error"}`}
                                        placeholder={t("eventCoupons.import.limit.placeholder")}
                                        value={importForm.limit}
                                        onChange={(e) => setImport({ limit: e.target.value.replace(/[^0-9]/g, "") })} />}
                                <label className="rev-switch-field" htmlFor="importUnlimited">
                                    <input
                                        type="checkbox"
                                        id="importUnlimited"
                                        className="rev-switch-input"
                                        checked={importForm.unlimited}
                                        onChange={(e) => setImport({ unlimited: e.target.checked })} />
                                    <RevSwitch on={importForm.unlimited} />
                                    <span>{t("eventCoupons.limit.unlimited")}</span>
                                </label>
                            </RevField>
                            <RevField label={t("eventCoupons.expiry.label")} htmlFor="importExpiry" help={t("eventCoupons.import.expiry.help")}>
                                <input
                                    id="importExpiry"
                                    type="date"
                                    min={today}
                                    className={`rev-input rev-num${importExpiryOk ? "" : " rev-input--error"}`}
                                    value={importForm.expiry}
                                    onChange={(e) => setImport({ expiry: e.target.value })} />
                            </RevField>
                        </div>
                        <RevField help={t("eventCoupons.import.update.help")}>
                            <label className="rev-switch-field" htmlFor="importUpdate">
                                <input
                                    type="checkbox"
                                    id="importUpdate"
                                    className="rev-switch-input"
                                    checked={importForm.update}
                                    onChange={(e) => setImport({ update: e.target.checked })} />
                                <RevSwitch on={importForm.update} />
                                <span>{t("eventCoupons.import.update.label")}</span>
                            </label>
                        </RevField>
                        <RevAlert tone="info" title={t("eventCoupons.import.notes.title")}>
                            <ul>
                                <li>{t("eventCoupons.import.notes.groups")}</li>
                                <li>{t("eventCoupons.import.notes.duplicates")}</li>
                                <li>{t("eventCoupons.import.notes.time")}</li>
                            </ul>
                        </RevAlert>
                    </div>
                </RevModal>}
            {importResult &&
                <RevModal width={520} onClose={() => setImportResult(null)} closeLabel={t("eventCoupons.importResult.close")}
                    title={t(`eventCoupons.importResult.title.${importTone}`)}
                    footer={<RevButton variant="primary" onClick={() => setImportResult(null)}>{t("eventCoupons.importResult.close")}</RevButton>}>
                    <div className="rev-modal__body">
                        <RevAlert tone={importTone}>
                            {t(`eventCoupons.importResult.body.${importTone}`, { count: importTone === "ok" ? importSuccess + importUpdated : (importSummary.totalProcessed || 0), success: formatCount(importSuccess + importUpdated, i18n.language), total: formatCount(importSummary.totalProcessed || 0, i18n.language) })}
                        </RevAlert>
                        <div className="rev-cup-result">
                            <RevKvRow className="rev-cup-kv" k={t("eventCoupons.importResult.processed")} v={formatCount(importSummary.totalProcessed || 0, i18n.language)} num />
                            <RevKvRow className="rev-cup-kv" k={t("eventCoupons.importResult.imported")} v={formatCount(importSuccess, i18n.language)} num tone={importSuccess > 0 ? "ok" : undefined} />
                            {importUpdated > 0 &&
                                <RevKvRow className="rev-cup-kv" k={t("eventCoupons.importResult.updated")} v={formatCount(importUpdated, i18n.language)} num tone="ok" />}
                            <RevKvRow className="rev-cup-kv" k={t("eventCoupons.importResult.duplicates")} v={formatCount(importSummary.duplicateCount || 0, i18n.language)} num tone={importSummary.duplicateCount > 0 ? "warn" : undefined} />
                            <RevKvRow className="rev-cup-kv" k={t("eventCoupons.importResult.failed")} v={formatCount(importSummary.errorCount || 0, i18n.language)} num tone={importSummary.errorCount > 0 ? "no" : undefined} />
                            <RevKvRow className="rev-cup-kv" k={t("eventCoupons.importResult.groups")} v={formatCount(importSummary.groupsCreated || 0, i18n.language)} num />
                            {importSummary.importDuration !== undefined &&
                                <RevKvRow className="rev-cup-kv" k={t("eventCoupons.importResult.duration")} v={t("eventCoupons.importResult.seconds", { seconds: Number(importSummary.importDuration).toFixed(1) })} num />}
                        </div>
                        {importSuccess > 0 &&
                            <RevField label={t("eventCoupons.importResult.successSamples")}>
                                {sampleChips(importSummary.successSamples, importSuccess, "ok", (code) => code)}
                            </RevField>}
                        {importUpdated > 0 &&
                            <RevField label={t("eventCoupons.importResult.updatedSamples")} help={t("eventCoupons.importResult.updatedHelp")}>
                                {sampleChips(importSummary.updatedSamples, importUpdated, "ok", (code) => code)}
                            </RevField>}
                        {importSummary.duplicateCount > 0 &&
                            <RevField label={t("eventCoupons.importResult.duplicateSamples")} help={t("eventCoupons.importResult.duplicateHelp")}>
                                {sampleChips(importSummary.duplicateSamples, importSummary.duplicateCount, "warn", (code) => code)}
                            </RevField>}
                        {importSummary.errorCount > 0 &&
                            <RevField label={t("eventCoupons.importResult.errorSamples")} help={t("eventCoupons.importResult.errorHelp")}>
                                {sampleChips(importSummary.errorSamples, importSummary.errorCount, "err", (item) => item?.code || `#${item?.line ?? "?"}`, (item) => t(`eventCoupons.importResult.errorCode.${item?.error}`, { defaultValue: item?.error }))}
                            </RevField>}
                    </div>
                </RevModal>}
        </div>
    );
};

const mapStateToProps = state => (
    {
        user: state.usersReducer.user,
    }
);

export default connect(mapStateToProps)(EventCoupons);
